import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  approveOwnerProfile,
  getCustomerProfiles,
  getOwnerProfiles,
  rejectOwnerProfile,
  getPendingOwnerProfiles,
  setCustomerSuspended,
  setOwnerSuspended,
  deleteCustomerProfile,
  deleteOwnerProfile,
  getSession,
  logoutUser,
} from '../../data/authStore';
import { deleteFieldById, districts, fields, updateFieldById } from '../../data/mockData';
import { cancelBookingById, getAllBookings } from '../../data/bookingStore';
import { Field } from '../../types';
import { getAuditLogs } from '../../data/auditStore';

const AdminPanelPage: React.FC = () => {
  const session = getSession();
  const [refreshSeed, setRefreshSeed] = useState(0);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    district: '',
    address: '',
    phone: '',
    pricePerHour: 0,
    size: '7v7' as Field['size'],
    sizeOptions: ['7v7'] as Array<'5v5' | '6v6' | '7v7' | '8v8' | '9v9' | '10v10' | '11v11'>,
    turfType: '',
    slotIntervalMinutes: 60 as 60,
    availableTimeRanges: '',
    description: '',
  });

  void refreshSeed;
  const pendingProfiles = getPendingOwnerProfiles();
  const ownerProfiles = getOwnerProfiles();
  const customerProfiles = getCustomerProfiles();
  const allFields = [...fields];
  const allBookings = [...getAllBookings()].sort(
    (a, b) => a.date.localeCompare(b.date) || a.hour - b.hour
  );
  const auditLogs = getAuditLogs(100);
  const formatHourValue = (value: number) => {
    const totalMinutes = Math.round(value * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  if (!session || session.role !== 'admin') {
    return (
      <div className="bg-slate-50 min-h-screen flex items-center justify-center px-4">
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center max-w-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Yetkisiz Erişim</h2>
          <p className="text-sm text-gray-600 mb-4">Bu sayfayı sadece admin hesabı görebilir.</p>
          <Link to="/giris" className="text-primary-600 hover:underline text-sm">Giriş sayfasına git</Link>
        </div>
      </div>
    );
  }

  const startEditField = (field: Field) => {
    setEditingFieldId(field.id);
    setEditForm({
      name: field.name,
      district: field.district,
      address: field.address,
      phone: field.phone,
      pricePerHour: field.pricePerHour,
      size: field.size,
      sizeOptions: field.sizeOptions || [field.size],
      turfType: field.turfType,
      slotIntervalMinutes: field.slotIntervalMinutes || 60,
      availableTimeRanges: field.availableTimeRanges || '',
      description: field.description || '',
    });
  };

  const saveField = () => {
    if (!editingFieldId) return;
    updateFieldById(editingFieldId, {
      ...editForm,
      pricePerHour: Number(editForm.pricePerHour),
      openingHour: 0,
      closingHour: 24,
      sizeOptions: editForm.sizeOptions,
      slotIntervalMinutes: editForm.slotIntervalMinutes,
      availableTimeRanges: editForm.availableTimeRanges,
    });
    setEditingFieldId(null);
    setRefreshSeed((value) => value + 1);
  };

  const toggleFieldSuspended = (fieldId: string, nextSuspended: boolean) => {
    updateFieldById(fieldId, {
      isReservationPaused: nextSuspended,
      reservationPausedMessage: nextSuspended ? 'Bu saha geçici olarak askıya alındı.' : '',
    });
    setRefreshSeed((value) => value + 1);
  };

  const removeField = (fieldId: string) => {
    if (!window.confirm('Bu sahayı sistemden kaldırmak istediğinize emin misiniz?')) return;
    deleteFieldById(fieldId);
    setRefreshSeed((value) => value + 1);
  };

  const toggleOwnerSuspend = (ownerId: string, currentState: boolean) => {
    setOwnerSuspended(ownerId, !currentState);
    setRefreshSeed((value) => value + 1);
  };

  const toggleCustomerSuspend = (userId: string, currentState: boolean) => {
    setCustomerSuspended(userId, !currentState);
    setRefreshSeed((value) => value + 1);
  };

  const removeOwner = (ownerId: string) => {
    if (!window.confirm('Bu saha sahibini sistemden kaldırmak istediğinize emin misiniz?')) return;
    const ownedFields = fields.filter((item) => item.ownerId === ownerId).map((item) => item.id);
    ownedFields.forEach((fieldId) => deleteFieldById(fieldId));
    deleteOwnerProfile(ownerId);
    setRefreshSeed((value) => value + 1);
  };

  const removeCustomer = (userId: string) => {
    if (!window.confirm('Bu kullanıcıyı sistemden kaldırmak istediğinize emin misiniz?')) return;
    deleteCustomerProfile(userId);
    setRefreshSeed((value) => value + 1);
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Kontrol Paneli</h1>
            <p className="text-sm text-gray-600 mt-1">Saha sahiplerini onayla, saha bilgilerini değiştir, rezervasyonları yönet.</p>
          </div>
          <button
            type="button"
            onClick={() => {
              logoutUser();
              window.location.href = '/giris';
            }}
            className="rounded-lg border border-gray-300 bg-white text-gray-700 px-4 py-2 text-sm"
          >
            Çıkış Yap
          </button>
        </div>

        <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Saha Sahibi Onayları</h2>
          </div>
          {pendingProfiles.length === 0 ? (
            <div className="px-5 py-8 text-sm text-gray-500">Bekleyen onay bulunmuyor.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {pendingProfiles.map((profile) => (
                <div key={profile.id} className="px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{profile.fullName}</p>
                    <p className="text-xs text-gray-600">@{profile.username} • {profile.phone}</p>
                    <p className="text-xs text-gray-500 mt-1">İlçe: {profile.district}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        approveOwnerProfile(profile.id);
                        setRefreshSeed((value) => value + 1);
                      }}
                      className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm font-medium"
                    >
                      Onayla
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        rejectOwnerProfile(profile.id);
                        setRefreshSeed((value) => value + 1);
                      }}
                      className="rounded-lg bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 text-sm font-medium"
                    >
                      Reddet
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Audit Log (Son 100 işlem)</h2>
          </div>
          {auditLogs.length === 0 ? (
            <div className="px-5 py-8 text-sm text-gray-500">Audit kaydı yok.</div>
          ) : (
            <div className="divide-y divide-gray-200 max-h-80 overflow-auto">
              {auditLogs.map((log) => (
                <div key={log.id} className="px-5 py-3">
                  <p className="text-sm font-medium text-gray-900">{log.action}</p>
                  <p className="text-xs text-gray-600 mt-1">
                    {new Date(log.createdAt).toLocaleString('tr-TR')} • Aktör: {log.actorRole}
                    {log.actorId ? ` (${log.actorId})` : ''} • Hedef: {log.targetType}
                    {log.targetId ? ` (${log.targetId})` : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Tüm Sahalar</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {allFields.map((field) => (
              <div key={field.id} className="px-5 py-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{field.name}</p>
                    <p className="text-xs text-gray-600">
                      {field.district} • {(field.sizeOptions || [field.size]).join(', ')} • {field.pricePerHour}₺/saat
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => startEditField(field)}
                    className="rounded-lg border border-gray-300 bg-white text-gray-700 px-3 py-1.5 text-xs"
                  >
                    Bilgileri Düzenle
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleFieldSuspended(field.id, !Boolean(field.isReservationPaused))}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                      field.isReservationPaused ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'
                    }`}
                  >
                    {field.isReservationPaused ? 'Askıyı Kaldır' : 'Askıya Al'}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeField(field.id)}
                    className="rounded-lg bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 text-xs font-medium"
                  >
                    Sistemden Kaldır
                  </button>
                </div>

                {editingFieldId === field.id && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <input value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} className="bg-white border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Saha Adı" />
                    <select value={editForm.district} onChange={(e) => setEditForm((p) => ({ ...p, district: e.target.value }))} className="bg-white border border-gray-300 rounded px-3 py-2 text-sm">
                      {districts.map((district) => (
                        <option key={district} value={district}>İstanbul / {district}</option>
                      ))}
                    </select>
                    <input value={editForm.address} onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))} className="bg-white border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Adres" />
                    <input value={editForm.phone} onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))} className="bg-white border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Telefon" />
                    <input type="number" value={editForm.pricePerHour} onChange={(e) => setEditForm((p) => ({ ...p, pricePerHour: Number(e.target.value) }))} className="bg-white border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Saatlik ücret" />
                    <select value={editForm.size} onChange={(e) => setEditForm((p) => ({ ...p, size: e.target.value as Field['size'] }))} className="bg-white border border-gray-300 rounded px-3 py-2 text-sm">
                      <option value="5v5">5v5</option>
                      <option value="7v7">7v7</option>
                      <option value="11v11">11v11</option>
                    </select>
                    <input value={editForm.sizeOptions.join(', ')} onChange={(e) => setEditForm((p) => ({ ...p, sizeOptions: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) as Array<'5v5' | '6v6' | '7v7' | '8v8' | '9v9' | '10v10' | '11v11'> }))} className="bg-white border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Formatlar: 5v5,7v7,8v8" />
                    <input value={editForm.turfType} onChange={(e) => setEditForm((p) => ({ ...p, turfType: e.target.value }))} className="bg-white border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Zemin tipi" />
                    <div className="bg-gray-100 border border-gray-300 rounded px-3 py-2 text-sm text-gray-700">
                      Seans: 60 dk (tam saat)
                    </div>
                    <input value={editForm.availableTimeRanges} onChange={(e) => setEditForm((p) => ({ ...p, availableTimeRanges: e.target.value }))} className="bg-white border border-gray-300 rounded px-3 py-2 text-sm" placeholder="09:00-12:00,13:00-23:00" />
                    <input value={editForm.description} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} className="md:col-span-2 lg:col-span-3 bg-white border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Açıklama" />
                    <div className="md:col-span-2 lg:col-span-3 flex items-center gap-2">
                      <button type="button" onClick={saveField} className="rounded bg-primary-600 hover:bg-primary-700 text-white text-sm px-3 py-2">Kaydet</button>
                      <button type="button" onClick={() => setEditingFieldId(null)} className="rounded border border-gray-300 bg-white text-gray-700 text-sm px-3 py-2">Vazgeç</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Tüm Kullanıcılar</h2>
          </div>
          <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-600">Saha Sahibi: {ownerProfiles.length} • Normal Kullanıcı: {customerProfiles.length}</p>
          </div>

          <div className="divide-y divide-gray-200">
            {ownerProfiles.map((owner) => (
              <div key={owner.id} className="px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{owner.fullName} <span className="text-xs font-normal text-gray-500">(Saha Sahibi)</span></p>
                  <p className="text-xs text-gray-600">@{owner.username} • {owner.phone}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Durum: {owner.suspended ? 'Askıda' : owner.status === 'approved' ? 'Onaylı' : owner.status === 'pending' ? 'Onay Bekliyor' : 'Reddedildi'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleOwnerSuspend(owner.id, Boolean(owner.suspended))}
                    className={`rounded-lg px-4 py-2 text-sm font-medium ${
                      owner.suspended ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'
                    }`}
                  >
                    {owner.suspended ? 'Askıyı Kaldır' : 'Askıya Al'}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeOwner(owner.id)}
                    className="rounded-lg bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 text-sm font-medium"
                  >
                    Sistemden Kaldır
                  </button>
                </div>
              </div>
            ))}
            {customerProfiles.map((customer) => (
              <div key={customer.id} className="px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{customer.fullName} <span className="text-xs font-normal text-gray-500">(Normal Kullanıcı)</span></p>
                  <p className="text-xs text-gray-600">@{customer.username} • {customer.phone || '-'}</p>
                  <p className="text-xs text-gray-500 mt-1">Durum: {customer.suspended ? 'Askıda' : 'Aktif'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleCustomerSuspend(customer.id, Boolean(customer.suspended))}
                    className={`rounded-lg px-4 py-2 text-sm font-medium ${
                      customer.suspended ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'
                    }`}
                  >
                    {customer.suspended ? 'Askıyı Kaldır' : 'Askıya Al'}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeCustomer(customer.id)}
                    className="rounded-lg bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 text-sm font-medium"
                  >
                    Sistemden Kaldır
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Tüm Rezervasyonlar (Tarih/Saat)</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {allBookings.map((booking) => (
              <div key={booking.id} className="px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{booking.fieldName}</p>
                  <p className="text-xs text-gray-600">
                    {booking.date} • {formatHourValue(booking.hour)} - {formatHourValue(booking.hour + booking.duration)} • {booking.teamName}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Durum: {booking.status}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-primary-700">{booking.totalPrice}₺</span>
                  <button
                    type="button"
                    disabled={booking.status === 'cancelled'}
                    onClick={() => {
                      cancelBookingById(booking.id);
                      setRefreshSeed((value) => value + 1);
                    }}
                    className="rounded-lg bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40 px-3 py-1.5 text-xs"
                  >
                    Maçı İptal Et
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminPanelPage;
